import React, { useState } from "react";
import { Box, Button, Typography, Grid, Avatar, IconButton } from "@mui/material";
import TableComponent from "components/grid";
import CustomPagination from "components/CustomPagination";
import StatusBadge from "components/badge/StatusBadge";
import moment from "moment";
import { LuEye } from "react-icons/lu";
import WorkflowHistoryDetail from "./WorkflowHistoryDetail";
import { useNavigate, useParams } from "react-router-dom";
import { useGetWorkflowInstancesQuery } from "store/slices/apis/workFlowSlice";
import { IoArrowBackOutline } from "react-icons/io5";

const WorkflowHistory = () => {

    const navigate = useNavigate();
    const { projectId, entityName } = useParams();
    const [isView, setIsView] = useState(false);
    const [viewData, setViewData] = useState(null);
    const [selectedProjectId, setSelectedProjectId] = useState(null);

    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sortOrder, setSortOrder] = useState("desc");
    const [sortBy, setSortBy] = useState("createdDate");
    const [showMspSchedule, setShowMspSchedule] = useState(false);


    const { data: scheduleList, isFetching, refetch } = useGetWorkflowInstancesQuery({ projectId: projectId, entityType: entityName });

    // const {
    //     data: workflowHistoryData,
    //     isLoading: isHistoryLoading,
    //     isError: isHistoryError
    // } = useGetWorkflowHistoryImplementationScheduleQuery(
    //     {
    //         recordId: selectedProjectId,
    //         moduleName: entityName
    //     },
    //     {
    //         skip: !selectedProjectId
    //     }
    // );

    React.useEffect(() => {
        // still loading / no data yet
        if (!scheduleList) {
            setShowMspSchedule(false);
            return;
        }

        let items = [];

        // case 1: API returns array directly
        if (Array.isArray(scheduleList)) {
            items = scheduleList;
        }
        // case 2: API returns { data: [...] }
        else if (Array.isArray(scheduleList.data)) {
            items = scheduleList.data;
        }
        // case 3: API returns { data: { items: [...] } }
        else if (Array.isArray(scheduleList.data?.items)) {
            items = scheduleList.data.items;
        }

        if (!items.length) {
            setShowMspSchedule(false);
            return;
        }

        // check if ANY item is approved (case-insensitive)
        const hasApproved = items.some((item) => {
            const status = item.status ?? item.Status ?? "";
            return typeof status === "string" && status.toLowerCase() === "approved";
        });

        setShowMspSchedule(hasApproved);
    }, [scheduleList]);

    const getAvatarColor = (title) => {
        const colors = ["#1976D2", "#388E3C", "#F57C00", "#7B1FA2", "#0288D1", "#C2185B"];
        const index = title ? title.charCodeAt(0) % colors.length : 0;
        return colors[index];
    };

    const handleViewClick = (row) => {
        setIsView((prev) => !prev);
        setViewData(row);
        if (row.id || row.projectId) {
            setSelectedProjectId(row.id || row.masterScheduleId);
        }
    };

    const handleViewClose = () => {
        setIsView(false);
        setViewData(null);
        setSelectedProjectId(null);
    };
    const getSortedData = () => {
        if (!scheduleList?.data) return [];

        return [...scheduleList.data].sort((a, b) => {
            const aValue = a[sortBy];
            const bValue = b[sortBy];


            if (sortBy === 'createdDate') {
                const aTime = new Date(aValue).getTime();
                const bTime = new Date(bValue).getTime();
                return sortOrder === 'asc' ? aTime - bTime : bTime - aTime;
            }


            else if (!isNaN(aValue) && !isNaN(bValue)) {
                const aNum = Number(aValue);
                const bNum = Number(bValue);
                return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
            }


            else {
                const aStr = String(aValue || '');
                const bStr = String(bValue || '');
                return sortOrder === 'asc'
                    ? aStr.localeCompare(bStr)
                    : bStr.localeCompare(aStr);
            }
        });
    };

    const handlePageChange = (newPage) => {
        setPageNumber(newPage);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPageNumber(1);
    };

    const handleRequestSort = (property) => {
        const isAsc = sortBy === property && sortOrder === "asc";
        setSortOrder(isAsc ? "desc" : "asc");
        setSortBy(property);
    };

    const columns = [
        {
            name: "Description",
            field: "description",
            width: "300px",
            renderer: (row) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <Avatar
                        sx={{
                            width: 30,
                            height: 30,
                            bgcolor: getAvatarColor(row.description),
                            color: "#fff",
                            fontSize: "14px",
                            fontWeight: "bold",
                            textTransform: "uppercase",
                        }}
                    >
                        {row?.description?.[0] || "?"}
                    </Avatar>
                    <Box>
                        <Typography
                            sx={{
                                fontSize: "16px",
                                maxWidth: "250px",
                                fontWeight: 500,
                                fontFamily: "Outfit",
                                color: "#433F5E",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {row?.description}
                        </Typography>
                    </Box>

                    <Box className="hoverButtons" sx={{ display: "none", gap: "10px" }}>

                        <Button
                            onClick={() => handleViewClick(row)}
                            startIcon={<LuEye style={{ width: 12, height: 12 }} />}
                            sx={{
                                backgroundColor: "#E7E7EB",
                                p: "2px 6px",
                                borderRadius: "5px",
                                fontSize: "11px",
                                fontWeight: 600,
                                textTransform: "none",
                            }}
                        >
                            View
                        </Button>
                    </Box>
                </Box>
            ),
        },
        {
            name: "Started On",
            field: "createdDate",
            renderer: (row) =>
                row?.createdDate ? moment(row.createdDate).format("DD MMM YYYY") : "N/A",
        },
        {
            name: "Started By",
            field: "createdBy",
        },
        {
            name: "Current Stage",
            field: "currentStep",
        },
        {
            name: "Status",
            field: "status",
            renderer: (row) => <StatusBadge status={row.status} />,
        },
    ];

    return (
        <>
            <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid size={12}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: "6px",
                            width: "100%"
                        }}
                    >
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>

                            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <IconButton
                                    onClick={() => navigate(-1)} // Goes back to the previous page
                                    sx={{ color: "#252C32" }}
                                >
                                    <IoArrowBackOutline sx={{ fontSize: { xs: "24px", sm: "32px" } }} />
                                </IconButton>

                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: { xs: "18px", sm: "32px", md: "36px" },
                                        lineHeight: { xs: "1.2", md: "48px" }, // Changed 16px to 1.2 for better vertical alignment
                                        color: "#252C32",
                                        handleOverflow: "hidden",
                                        whiteSpace: "nowrap",
                                        textOverflow: "ellipsis",
                                        fontFamily: "Outfit, sans-serif",
                                    }}
                                >
                                    {entityName} - Execution Summary
                                </Typography>
                            </Box>
                            <Typography
                                sx={{
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    lineHeight: "24px",
                                    color: "#252C32",
                                    fontFamily: "Outfit, sans-serif",
                                }}
                            >
                                View how frequently workflows have been triggered and executed over time.
                            </Typography>
                        </Box>
                    </Box>
                </Grid>
                <Grid size={12}>
                    <TableComponent
                        data={getSortedData()}
                        isLoading={isFetching}
                        columns={columns}
                        order={sortOrder}
                        orderBy={sortBy}
                        createSortHandler={handleRequestSort}
                    />
                    <CustomPagination
                        totalCount={scheduleList?.totalRecords || 0}
                        pageSize={pageSize}
                        page={pageNumber}
                        onPageChange={handlePageChange}
                        setCurrentPage={setPageNumber}
                        onPageSizeChange={handlePageSizeChange}
                    />
                </Grid>
            </Grid >

            {isView && (
                <WorkflowHistoryDetail
                    open={isView}
                    onClose={handleViewClose}
                    cardData={viewData}
                    // isHistoryLoading={isHistoryLoading}
                    isFalse={false}
                />
            )
            }
        </>
    );
};

export default WorkflowHistory;